import { CONFIG } from '@utils/constants';

/**
 * Simple Unit Validator
 * Only reports problems, stays silent when everything is OK
 */

// Properties that should exist on the unit object itself
const UNIT_PROPERTIES = [
  'id',
  'name', 
  'x',
  'y',
  'hp',
  'ap',
  'movement',
  'has_acted',
  'is_alive',
  'player_id',
  'unit_template_id',
  'status',
  'teamClass',
  'type'
];

// Properties that should exist in unit.unit_template
const TEMPLATE_PROPERTIES = [
  'max_hp',
  'max_ap',
  'max_movement',
  'armor',
  'will',
  'vision',
  'strength',
  'agility'
];

/**
 * Validates units array - only reports problems
 * Silent when everything is OK
 */
export function validateUnits(units, source = 'unknown') {
  // Only run in debug mode
  if (!CONFIG.DEBUG_MODE) return;
  
  // Check if we have a valid array
  if (!Array.isArray(units)) {
    console.warn(`[Unit Validator] Expected array from ${source}, got:`, typeof units);
    return;
  }
  
  // Check each unit for missing properties
  units.forEach(unit => {
    if (!unit) {
      console.warn(`[Unit Validator] Null unit in ${source}`);
      return;
    }
    
    const missing = [];
    
    // Check properties that should be on the unit itself
    UNIT_PROPERTIES.forEach(prop => {
      if (unit[prop] === undefined || unit[prop] === null) {
        missing.push(prop);
      }
    });
    
    // Check template properties
    if (!unit.unit_template) {
      missing.push('unit_template (entire object missing)');
    } else {
      TEMPLATE_PROPERTIES.forEach(prop => {
        if (unit.unit_template[prop] === undefined || unit.unit_template[prop] === null) {
          missing.push(`unit_template.${prop}`);
        }
      });
    }
    
    // Only log if there are problems
    if (missing.length > 0) {
      console.warn(`[Unit Validator] Unit #${unit.id || 'unknown'} from ${source} missing:`, missing);
    }
  });
}

/**
 * Legacy functions kept for compatibility
 * These just call the main validateUnits function
 */
export function validateGameSnapshots(game) {
  if (!CONFIG.DEBUG_MODE || !game) return;
  
  // Check units in current view
  if (game.dynamic_objects?.units) {
    validateUnits(game.dynamic_objects.units, 'current game');
  }
  
  // Check snapshots if they exist
  if (game.snapshots) {
    if (game.snapshots.base?.dynamic_objects?.units) {
      validateUnits(game.snapshots.base.dynamic_objects.units, 'base snapshot');
    }
    if (game.snapshots.current?.dynamic_objects?.units) {
      validateUnits(game.snapshots.current.dynamic_objects.units, 'current snapshot');
    }
  }
}

export function validateUnitAcrossSnapshots(game, unitId) {
  // Not needed in simplified version
  if (!CONFIG.DEBUG_MODE) return;
  console.log(`[Unit Validator] Unit #${unitId} validation across snapshots not implemented in simple version`);
}

export function validateUnit(unit) {
  // Legacy function for compatibility - just check and log
  if (!CONFIG.DEBUG_MODE) return;
  
  if (!unit) {
    console.warn('[Unit Validator] validateUnit called with null unit');
    return;
  }
  
  validateUnits([unit], 'single unit check');
}